<script setup>
import { DateInput } from '@addictivegroup/addictive-datetime'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api } from '@/lib/http.js'
import { formatDate, formatMoney } from '@/lib/format.js'
import { reportError } from '@/composables/useSave.js'
import { useUi } from '@/stores/ui.js'
import UiBadge from '@/components/ui/UiBadge.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiModal from '@/components/ui/UiModal.vue'

/**
 * Lines of the billing schedule (D-216) — in the "To invoice" report and on a contract's or an
 * agreement's page (`compact`: without the contract and the customer): the chosen ones marked
 * invoiced with the invoice's number and date (one invoice may cover several), a mark taken back.
 * Marking takes `contract:invoice`.
 */
const props = defineProps({ lines: { type: Array, default: () => [] }, canInvoice: Boolean, compact: Boolean, currency: { type: String, default: 'PLN' } })
const emit = defineEmits(['changed'])
const { t } = useI18n()
const ui = useUi()
const chosen = ref([])
const marking = ref(null)
const saving = ref(false)
watch(() => props.lines, () => (chosen.value = []))

const TONES = { due: 'amber', overdue: 'red', invoiced: 'green', planned: 'gray' }
const money = (v, code) => formatMoney(v, code || props.currency)
const markable = (l) => props.canInvoice && l.status !== 'invoiced' && !(l.kind === 'stage' && l.status === 'planned')
const choosable = computed(() => props.lines.filter(markable))
const all = computed({
  get: () => choosable.value.length > 0 && choosable.value.every((l) => chosen.value.includes(l.key)),
  set: (v) => (chosen.value = v ? choosable.value.map((l) => l.key) : []),
})
const picked = computed(() => props.lines.filter((l) => chosen.value.includes(l.key)))
const pickedSum = computed(() => Object.entries(picked.value.reduce((m, l) => ({ ...m, [l.currency]: (m[l.currency] || 0) + l.net }), {})).map(([c, v]) => money(v, c)).join(' + '))
const columns = computed(() => (props.canInvoice ? 1 : 0) + (props.compact ? 5 : 7))

function mark() {
  marking.value = { number: '', invoiced_on: new Date().toISOString().slice(0, 10), note: '' }
}
async function save() {
  saving.value = true
  try {
    await api('/billing/invoices', { method: 'POST', body: { keys: chosen.value, ...marking.value } })
    ui.success(t('billing.marked', { n: chosen.value.length, number: marking.value.number }))
    marking.value = null
    emit('changed')
  } catch (e) {
    reportError(e)
  } finally {
    saving.value = false
  }
}
async function unmark(l) {
  if (!(await ui.ask({ title: l.invoice.number, message: t('billing.unmarkConfirm', { line: l.description }), confirmLabel: t('billing.unmark'), danger: true }))) return
  try {
    await api(`/billing/invoices/${l.invoice.uuid}`, { method: 'DELETE' })
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
const kind = (l) => t(`billing.kinds.${l.kind}`)
</script>

<template>
  <div class="space-y-3">
    <div v-if="canInvoice && chosen.length" class="card flex flex-wrap items-center gap-3 p-3" data-testid="billing-chosen">
      <span class="text-sm">{{ $t('billing.chosen', { n: chosen.length }) }} · <span class="font-semibold tabular-nums">{{ pickedSum }}</span></span>
      <UiButton variant="primary" icon="fa-file-invoice" data-testid="billing-mark" @click="mark">{{ $t('billing.mark') }}</UiButton>
    </div>
    <div class="overflow-x-auto">
      <table class="table-crm" data-testid="billing-lines">
        <thead>
          <tr>
            <th v-if="canInvoice" class="w-8"><input v-model="all" type="checkbox" :aria-label="$t('billing.chooseAll')" :disabled="!choosable.length" /></th>
            <th>{{ $t('billing.dueOn') }}</th><th>{{ $t('billing.status') }}</th>
            <template v-if="!compact"><th>{{ $t('entities.contract.one') }}</th><th>{{ $t('billing.account') }}</th></template>
            <th>{{ $t('billing.what') }}</th><th class="text-right">{{ $t('billing.net') }}</th><th>{{ $t('billing.invoice') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="l in lines" :key="l.key" data-testid="billing-line" :data-key="l.key">
            <td v-if="canInvoice"><input v-if="markable(l)" v-model="chosen" type="checkbox" :value="l.key" :aria-label="l.description" /></td>
            <td class="whitespace-nowrap tabular-nums">{{ formatDate(l.due_on) }}</td>
            <td class="whitespace-nowrap"><UiBadge :tone="TONES[l.status]">{{ $t(`billing.states.${l.status}`) }}</UiBadge><span v-if="l.days_overdue" class="ml-1 text-xs text-red-600">{{ $t('billing.daysOverdue', { n: l.days_overdue }) }}</span></td>
            <template v-if="!compact">
              <td class="whitespace-nowrap"><router-link class="link" :to="{ name: 'record', params: { entityPath: 'contracts', uuid: l.contract_uuid } }">{{ l.contract || l.contract_title }}</router-link><span v-if="l.agreement" class="block text-xs text-faint">{{ l.agreement }}</span></td>
              <td>{{ l.account }}<span v-if="l.tax_id" class="block text-xs text-faint">{{ $t('billing.taxId') }} {{ l.tax_id }}</span></td>
            </template>
            <td>{{ l.description }} <span class="text-xs text-faint">· {{ kind(l) }}{{ l.period ? ` ${l.period}` : '' }}</span><span v-if="compact && l.agreement" class="block text-xs text-faint">{{ l.agreement }}</span></td>
            <td class="whitespace-nowrap text-right tabular-nums">{{ money(l.net, l.currency) }}</td>
            <td class="whitespace-nowrap">
              <template v-if="l.invoice">
                <span class="font-medium">{{ l.invoice.number }}</span> <span class="text-xs text-muted">{{ formatDate(l.invoice.invoiced_on) }}</span>
                <UiBadge v-if="Math.abs(l.invoice.difference) >= 0.01" tone="amber" :title="$t('billing.differenceHint')">{{ $t('billing.difference', { amount: money(l.invoice.difference, l.currency) }) }}</UiBadge>
                <UiButton v-if="canInvoice" size="xs" variant="ghost" icon="fa-rotate-left" :title="$t('billing.unmark')" data-testid="billing-unmark" @click="unmark(l)" />
              </template>
              <span v-else class="text-faint">—</span>
            </td>
          </tr>
          <tr v-if="!lines.length"><td :colspan="columns" class="text-faint">{{ $t('billing.none') }}</td></tr>
        </tbody>
      </table>
    </div>

    <UiModal :open="!!marking" :title="$t('billing.mark')" size="sm" @close="marking = null">
      <form v-if="marking" id="billing-mark-form" class="space-y-3 text-sm" @submit.prevent="save">
        <p class="text-muted">{{ $t('billing.markHint', { n: chosen.length, amount: pickedSum }) }}</p>
        <label class="block"><span class="label">{{ $t('billing.number') }}</span><input v-model="marking.number" class="input" required maxlength="64" data-testid="billing-number" /></label>
        <label class="block"><span class="label">{{ $t('billing.invoicedOn') }}</span><DateInput v-model="marking.invoiced_on" direction="past" :clearable="false" data-testid="invoiced-on" /></label>
        <label class="block"><span class="label">{{ $t('billing.note') }}</span><textarea v-model="marking.note" class="input" rows="2" maxlength="2000"></textarea></label>
      </form>
      <template #footer>
        <UiButton @click="marking = null">{{ $t('common.cancel') }}</UiButton>
        <UiButton variant="primary" type="submit" form="billing-mark-form" :loading="saving" data-testid="billing-save">{{ $t('common.save') }}</UiButton>
      </template>
    </UiModal>
  </div>
</template>
